import { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { bookKey, getBook, updateBook, writerOptions, type BookWriter, type Book } from "../api";
import { useT } from "../i18n";
import {
  canMove,
  choicesOf,
  entriesOf,
  move,
  withAdded,
  withRole,
  without,
  type WriterEntry,
} from "./writers";

const pill = (on: boolean) =>
  `rounded-full border px-3 py-1 text-xs transition-colors ${
    on ? "border-sage bg-sage-soft font-medium" : "border-line hover:bg-sunk"
  }`;

/**
 * BookWritersEditor sets who a book names, and as what (requirement 0118 W2):
 * authors and translators, in order, picked from the writers this library can
 * already see or created by name. Pill chips throughout — no <select> on the
 * phone.
 *
 * Every change saves the whole list at once (`writers` on the book's PATCH),
 * so the order on screen is the order the server numbers. SageLeaf's own writer
 * is never found by typing its name; it is offered on its own, and only while
 * adding a translator, because a machine translation is the one thing it did.
 */
export default function BookWritersEditor({ bookID }: { bookID: string }) {
  const t = useT();
  const client = useQueryClient();
  const book = useQuery({
    queryKey: bookKey(bookID, { draft: true }),
    queryFn: () => getBook(bookID, { draft: true }),
  });
  const [query, setQuery] = useState("");
  const [role, setRole] = useState<BookWriter["role"]>("author");
  const options = useQuery({
    queryKey: ["writer-options", bookID, query.trim()],
    queryFn: () => writerOptions(bookID, query.trim()),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
  const save = useMutation({
    mutationFn: (list: WriterEntry[]) => updateBook(bookID, { writers: choicesOf(list) }),
    onSuccess: (updated: Book) => {
      client.setQueryData(bookKey(bookID, { draft: true }), updated);
      void client.invalidateQueries({ queryKey: ["book", bookID] });
      void client.invalidateQueries({ queryKey: ["writer-options", bookID] });
    },
  });

  const list = entriesOf(book.data?.writers);
  const busy = save.isPending || book.isPending;
  const commit = (next: WriterEntry[]) => {
    if (next !== list) save.mutate(next);
  };
  const add = (entry: WriterEntry) => {
    commit(withAdded(list, entry));
    setQuery("");
  };

  const typed = query.trim();
  const found = (options.data?.items ?? []).filter(
    (w) => !list.some((e) => e.id === w.id && e.role === role),
  );
  const exact = found.some((w) => w.name.toLowerCase() === typed.toLowerCase());
  const sageleaf = options.data?.sageleaf;
  const sageleafNamed = !!sageleaf && list.some((e) => e.id === sageleaf.id);

  return (
    <div data-testid="book-writers-editor">
      <p className="block text-sm font-medium">{t.writer.writers}</p>
      <p className="mt-1 mb-3 text-xs text-muted">{t.writer.writersNote}</p>

      {list.length === 0 && !book.isPending && (
        <p data-testid="writers-none" className="mb-3 text-sm text-muted">
          {t.writer.none}
        </p>
      )}
      <ul className="mb-4 divide-y divide-line rounded-card border border-line bg-surface empty:hidden">
        {list.map((w, i) => (
          <li
            key={`${w.role}-${w.id || w.name}`}
            data-testid="writer-row"
            data-role={w.role}
            className="flex flex-wrap items-center gap-2 px-3 py-2"
          >
            {/* Authored text: dir="auto" here and on nothing inside it. */}
            <span
              dir="auto"
              data-testid="writer-row-name"
              className="min-w-0 flex-1 truncate text-sm"
            >
              {w.name}
            </span>
            <span className="flex gap-1">
              {(["author", "translator"] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`writer-role-${r}`}
                  aria-pressed={w.role === r}
                  disabled={busy || (r === "author" && w.id === sageleaf?.id)}
                  onClick={() => commit(withRole(list, i, r))}
                  className={pill(w.role === r)}
                >
                  {r === "author" ? t.writer.author : t.writer.translator}
                </button>
              ))}
            </span>
            <span className="flex items-center gap-1 text-muted">
              <button
                type="button"
                aria-label={t.writer.moveUp}
                data-testid="writer-up"
                disabled={busy || !canMove(list, i, -1)}
                onClick={() => commit(move(list, i, -1))}
                className="rounded-control px-2 py-1 hover:bg-sunk disabled:opacity-30"
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={t.writer.moveDown}
                data-testid="writer-down"
                disabled={busy || !canMove(list, i, 1)}
                onClick={() => commit(move(list, i, 1))}
                className="rounded-control px-2 py-1 hover:bg-sunk disabled:opacity-30"
              >
                ↓
              </button>
              {w.id && w.id !== sageleaf?.id && (
                <Link
                  to={`/studio/writers/${w.id}?book=${bookID}`}
                  data-testid="writer-edit"
                  className="rounded-control px-2 py-1 text-xs hover:bg-sunk"
                >
                  {t.writer.edit}
                </Link>
              )}
              <button
                type="button"
                aria-label={t.writer.remove}
                data-testid="writer-remove"
                disabled={busy}
                onClick={() => commit(without(list, i))}
                className="rounded-control px-2 py-1 hover:bg-sunk"
              >
                ×
              </button>
            </span>
          </li>
        ))}
      </ul>

      <p className="mb-2 text-xs font-medium text-muted">{t.writer.add}</p>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted">{t.writer.addAs}</span>
        {(["author", "translator"] as const).map((r) => (
          <button
            key={r}
            type="button"
            data-testid={`writer-add-as-${r}`}
            aria-pressed={role === r}
            onClick={() => setRole(r)}
            className={pill(role === r)}
          >
            {r === "author" ? t.writer.author : t.writer.translator}
          </button>
        ))}
      </div>
      <input
        dir="auto"
        type="search"
        data-testid="writer-search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t.writer.search}
        className="mb-2 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm"
      />
      <div className="flex flex-wrap gap-2" data-testid="writer-options">
        {found.map((w) => (
          <button
            key={w.id}
            type="button"
            dir="auto"
            data-testid="writer-option"
            disabled={busy}
            onClick={() => add({ id: w.id, name: w.name, role })}
            className={pill(false)}
          >
            {w.name}
            {w.dates && <span className="text-muted"> · {w.dates}</span>}
          </button>
        ))}
        {typed && !exact && (
          <button
            type="button"
            data-testid="writer-create"
            disabled={busy}
            onClick={() => add({ id: "", name: typed, role })}
            className={pill(false)}
          >
            {t.writer.create(typed)}
          </button>
        )}
        {role === "translator" && sageleaf && !sageleafNamed && (
          <button
            type="button"
            data-testid="writer-sageleaf"
            disabled={busy}
            onClick={() => add({ id: sageleaf.id, name: sageleaf.name, role: "translator" })}
            className={pill(false)}
          >
            {sageleaf.name} <span className="text-muted">· {t.writer.sageleafNote}</span>
          </button>
        )}
      </div>
      {options.isError && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
          {t.writer.searchFailed}
        </p>
      )}
      {save.isError && (
        <p
          role="alert"
          data-testid="writers-error"
          className="mt-2 text-sm text-red-600 dark:text-red-400"
        >
          {save.error instanceof Error ? save.error.message : t.writer.saveFailed}
        </p>
      )}
    </div>
  );
}
